/**
 * User Proxy
 * Reactive wrapper for User model in React
 */

import { User, UserData } from './User';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { UserFactory } from './UserFactory';

/**
 * Proxy class that wraps User for reactive updates
 */
export class UserProxy {
  private user: User;
  private listeners: Set<() => void> = new Set();
  private _isDirty: boolean = false;
  private originalData: UserData;

  constructor(user: User) {
    this.user = user;
    this.originalData = user.toJSON();
  }

  // ===== Proxy all User properties =====

  get id(): string { return this.user.id; }
  get username(): string { return this.user.username; }
  get email(): string | undefined { return this.user.email; }
  get profile() { return this.user.profile; }
  get roles() { return this.user.roles; }
  get balances() { return this.user.balances; }
  get otherProfiles() { return this.user.otherProfiles; }
  get isGuest() { return this.user.isGuest; }
  
  // Computed properties
  get fullName() { return this.user.fullName; }
  get displayName() { return this.user.displayName; }
  get initials() { return this.user.initials; }
  get totalBalance() { return this.user.totalBalance; }
  get primaryCurrency() { return this.user.primaryCurrency; }
  
  // Role checks
  get isMerchant() { return this.user.isMerchant; }
  get isAdmin() { return this.user.isAdmin; }
  get isSupport() { return this.user.isSupport; }
  get isDeveloper() { return this.user.isDeveloper; }
  get isBank() { return this.user.isBank; }
  
  // Merchant properties
  get merchantProfile() { return this.user.merchantProfile; }
  get hasMerchantProfile() { return this.user.hasMerchantProfile; }
  get isMerchantOnboarded() { return this.user.isMerchantOnboarded; }
  get merchantBusinessName() { return this.user.merchantBusinessName; }
  
  // Profile completeness
  get profileCompleteness() { return this.user.profileCompleteness; }
  get isProfileComplete() { return this.user.isProfileComplete; }
  get missingProfileFields() { return this.user.missingProfileFields; }

  // ===== Proxy User methods =====

  hasRole(role: string): boolean {
    return this.user.hasRole(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    return this.user.hasAnyRole(...roles);
  }

  hasAllRoles(...roles: string[]): boolean {
    return this.user.hasAllRoles(...roles);
  }

  getBalance(currency: string): number {
    return this.user.getBalance(currency);
  }

  hasBalance(currency: string, amount: number): boolean {
    return this.user.hasBalance(currency, amount);
  }

  getCurrencies(): string[] {
    return this.user.getCurrencies();
  }

  hasAnyCurrency(): boolean {
    return this.user.hasAnyCurrency();
  }

  canAccessMerchantTools(): boolean {
    return this.user.canAccessMerchantTools();
  }

  canAccessAdminPanel(): boolean {
    return this.user.canAccessAdminPanel();
  }

  canManageUsers(): boolean {
    return this.user.canManageUsers();
  }

  canViewAnalytics(): boolean {
    return this.user.canViewAnalytics();
  }

  canCreateTokens(): boolean {
    return this.user.canCreateTokens();
  }

  canTransferFunds(): boolean {
    return this.user.canTransferFunds();
  }

  // ===== Update methods with change tracking =====

  updateProfile(updates: Partial<UserData['profile']>): void {
    this.user = this.user.updateProfile(updates);
    this._isDirty = true;
    this.notifyListeners();
  }

  updateBalances(balances: UserData['balances']): void {
    this.user = this.user.updateBalances(balances);
    this._isDirty = true;
    this.notifyListeners();
  }

  addRole(role: string): void {
    this.user = this.user.addRole(role);
    this._isDirty = true;
    this.notifyListeners();
  }

  removeRole(role: string): void {
    this.user = this.user.removeRole(role);
    this._isDirty = true;
    this.notifyListeners();
  }

  // ===== Change tracking =====

  get isDirty(): boolean {
    return this._isDirty;
  }

  getChanges(): Partial<UserData> {
    if (!this._isDirty) return {};
    
    const current = this.user.toJSON();
    const changes: Partial<UserData> = {};
    
    // Compare and track changes
    if (current.profile !== this.originalData.profile) {
      changes.profile = current.profile;
    }
    if (JSON.stringify(current.roles) !== JSON.stringify(this.originalData.roles)) {
      changes.roles = current.roles;
    }
    if (JSON.stringify(current.balances) !== JSON.stringify(this.originalData.balances)) {
      changes.balances = current.balances;
    }
    
    return changes;
  }

  resetChanges(): void {
    this.user = new User(this.originalData);
    this._isDirty = false;
    this.notifyListeners();
  }

  commitChanges(): void {
    this.originalData = this.user.toJSON();
    this._isDirty = false;
  }

  // ===== Listener management =====

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  // ===== Utility methods =====

  getUser(): User {
    return this.user;
  }

  setUser(user: User): void {
    this.user = user;
    this.originalData = user.toJSON();
    this._isDirty = false;
    this.notifyListeners();
  }

  toJSON(): UserData {
    return this.user.toJSON();
  }

  toPublicJSON(): Partial<UserData> {
    return this.user.toPublicJSON();
  }
}

/**
 * React hook for using UserProxy
 */
export function useUserProxy(initialUser: User | null): {
  userProxy: UserProxy | null;
  updateUser: (user: User) => void;
  isDirty: boolean;
  saveChanges: () => Promise<void>;
  resetChanges: () => void;
} {
  const [userProxy, setUserProxy] = useState<UserProxy | null>(
    initialUser ? new UserProxy(initialUser) : null
  );
  const [, forceUpdate] = useState({});

  // Subscribe to proxy changes
  useEffect(() => {
    if (!userProxy) return;
    
    const unsubscribe = userProxy.subscribe(() => {
      forceUpdate({});
    });
    
    return unsubscribe;
  }, [userProxy]);

  const updateUser = useCallback((user: User) => {
    if (userProxy) {
      userProxy.setUser(user);
    } else {
      setUserProxy(new UserProxy(user));
    }
  }, [userProxy]);

  const saveChanges = useCallback(async () => {
    if (!userProxy || !userProxy.isDirty) return;
    
    const changes = userProxy.getChanges();
    
    // This would call the API to save changes
    // await platformApi.updateProfile(changes);
    
    userProxy.commitChanges();
  }, [userProxy]);

  const resetChanges = useCallback(() => {
    userProxy?.resetChanges();
  }, [userProxy]);

  return {
    userProxy,
    updateUser,
    isDirty: userProxy?.isDirty || false,
    saveChanges,
    resetChanges
  };
}

/**
 * React hook for user permissions
 */
export function useUserPermissions(user: User | null): {
  can: (action: string) => boolean;
  cannot: (action: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  hasAllRoles: (...roles: string[]) => boolean;
} {
  return useMemo(() => {
    if (!user) {
      return {
        can: () => false,
        cannot: () => true,
        hasRole: () => false,
        hasAnyRole: () => false,
        hasAllRoles: () => false
      };
    }

    // Get primary role to check permissions
    const primaryRole = UserFactory.getPrimaryRole(user);

    return {
      can: (action: string) => primaryRole.canPerformAction(action),
      cannot: (action: string) => !primaryRole.canPerformAction(action),
      hasRole: (role: string) => user.hasRole(role),
      hasAnyRole: (...roles: string[]) => user.hasAnyRole(...roles),
      hasAllRoles: (...roles: string[]) => user.hasAllRoles(...roles)
    };
  }, [user]);
}
